import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ICON_SIZE } from '../../../global/constants';
import { WorkloadWeeklyScheduleInterface } from '../../../interfaces/ReportInterface';
import {
  CellContent,
  CheckmarkIcon,
  TimeBox,
  TimeHours,
  TimeLabel,
  WeekendText,
  WorkloadCell,
  WorkloadCellContainer
} from './styles';

const WorkloadDayCell: React.FC<{ daySchedule: WorkloadWeeklyScheduleInterface }> = ({ daySchedule }) => {
  // Helpers: parse "8h", "6h 30m", "00" → numeric hours
  const parseHoursToNumber = (hours?: string): number => {
    if (!hours || hours === '00') return 0;
    const hourMatch = hours.match(/(\d+)\s*h/i);
    const minuteMatch = hours.match(/(\d+)\s*m/i);
    const h = hourMatch ? parseInt(hourMatch[1], 10) : 0;
    const m = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
    return h + m / 60;
  };

  // Compute colors from hour rules (8h baseline)
  const getCellBackgroundColor = (hoursStr?: string, isWeekend?: boolean) => {
    const hours = parseHoursToNumber(hoursStr);
    const hasWork = hours > 0;
    if (!hasWork) return 'transparent'; // not started or weekend with no work
    if (hours > 8) return 'var(--red)'; // overtime
    if (hours === 8 || hours >= 6) return 'var(--green)'; // 8h or >=6h
    if (hours < 6) return 'var(--yellow)'; // under 6h
    return 'transparent';
  };

  const getCellTextColor = (hoursStr?: string, isWeekend?: boolean) => {
    const hours = parseHoursToNumber(hoursStr);
    const hasWork = hours > 0;
    if (!hasWork) return 'var(--text-placeholder)';
    if (hours > 8) return 'var(--red)';
    if (hours === 8 || hours > 6) return 'var(--green)';
    if (hours < 6) return 'var(--yellow)';
    return 'var(--text-placeholder)';
  };

  // Parse hours for CSS custom property (height calculation moved to CSS)
  const getHoursForCSS = (hoursStr?: string): number => {
    const hours = parseHoursToNumber(hoursStr);
    if (hours <= 0) return 0;
    // For hours > 8, calculate overtime proportion
    if (hours > 8) {
      const overtime = Math.min(hours - 8, 8);
      return overtime; // Return overtime hours for CSS calc
    }
    return Math.min(hours, 8); // Return effective hours (max 8)
  };

  // Format decimal hours to "Xh Ym"
  const formatHours = (decimalHours: number): string => {
    const h = Math.floor(decimalHours);
    const m = Math.round((decimalHours - h) * 60);
    if (h === 0 && m === 0) return '00';
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };
  return (
    <>
      <WorkloadCellContainer>
        <WorkloadCell
          backgroundColor={getCellBackgroundColor(daySchedule.hours, daySchedule.type === 'weekend')}
          textColor={getCellTextColor(daySchedule.hours, daySchedule.type === 'weekend')}
          isDimmed={!parseHoursToNumber(daySchedule.hours)}
          hours={getHoursForCSS(daySchedule.hours)}
          isOvertime={parseHoursToNumber(daySchedule.hours) > 8}>
          <CellContent isTextCenter={daySchedule.type === 'weekend' ? true : false}>
            {/* Weekend with no work → show Weekend text. If worked on weekend → show regular design. */}
            {parseHoursToNumber(daySchedule.hours) === 8 && (
              <CheckmarkIcon>
                <SVGIcon
                  name='updated-task-completed-icon'
                  width={ICON_SIZE.XS}
                  height={ICON_SIZE.XS}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                />
              </CheckmarkIcon>
            )}
            {!(daySchedule.type === 'weekend' && !parseHoursToNumber(daySchedule.hours)) ? (
              <TimeBox>
                <TimeHours
                  isOvertime={parseHoursToNumber(daySchedule.hours) > 8}
                  textColor={getCellTextColor(daySchedule.hours, daySchedule.type === 'weekend')}>
                  {parseHoursToNumber(daySchedule.hours) > 8
                    ? formatHours(parseHoursToNumber(daySchedule.hours) - 8)
                    : daySchedule.hours || '00'}
                </TimeHours>
                {parseHoursToNumber(daySchedule.hours) > 8 && <TimeLabel>Overtime</TimeLabel>}
              </TimeBox>
            ) : (
              <WeekendText>Weekend</WeekendText>
            )}
          </CellContent>
        </WorkloadCell>
      </WorkloadCellContainer>
    </>
  );
};

export default WorkloadDayCell;
